import { Box } from '@material-ui/core'
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft'
import { useState } from 'react'
import { useQuery } from 'react-query'
import { Wrap } from 'shared/components/Base/Base'
import { Button } from 'shared/components/Button/Button'
import { InlineSpinner } from 'shared/components/Spinner'
import { Text } from 'shared/components/Text/Text'
import { SafetyDashboardHeader } from 'shared/pages/Safety/SafetyDashboardHeader'
import { SafetyDashboardOosSummary } from 'shared/pages/Safety/SafetyDashboardOosSummary'
import { SafetyDashboardViolations } from 'shared/pages/Safety/SafetyDashboardViolations'
import { SafetyDashboardViolationsSummary } from 'shared/pages/Safety/SafetyDashboardViolationsSummary'
import { primaryBlack } from 'shared/theme/colors'

import { getSafetyDashboard } from '../../models/safety'
import { Team } from '../../models/team'

export function TeamSafetyDashboard({ team }: { team: Team }) {
  const [selectedSection, setSelectedSection] = useState<string | null>(null)
  const { safetyData, isLoading } = useTeamSafetyDashboard({
    enabled: Boolean(team.dot_number),
    teamId: team.id ?? '',
  })

  function handleGoBackClick() {
    setSelectedSection(null)
  }

  function handleHeaderClick() {
    setSelectedSection('oos')
  }

  function renderTitle() {
    return (
      <Text variant="header2" fontColor={primaryBlack}>
        Your fleet’s safety and compliance
      </Text>
    )
  }

  function renderContent() {
    if (!team.dot_number) {
      return <Text variant="paragraph1">No dot number for this team</Text>
    }

    if (isLoading) {
      return <InlineSpinner mt={5} mb={5} />
    }

    if (!safetyData) {
      return (
        <>
          {renderTitle()}
          <Box mt={4}>
            <Text variant="paragraph1" mt={0.6}>
              We couldn't fetch safety data for dot number: {team.dot_number}
            </Text>
          </Box>
        </>
      )
    }

    if (!safetyData.carrierOverview || !safetyData.violationsDetails) {
      return (
        <>
          {renderTitle()}
          <Text variant="paragraph1" mt={2}>
            DOT number is being processed. Check back soon.
          </Text>
        </>
      )
    }

    if (selectedSection) {
      return (
        <>
          <Button variant="text" onClick={handleGoBackClick} icon={<KeyboardArrowLeftIcon />}>
            Go back
          </Button>
          <Box mt={3} />
          <SafetyDashboardViolations
            selectedSection={selectedSection}
            violations={safetyData.violationsDetails}
          />
        </>
      )
    }

    return (
      <>
        <SafetyDashboardHeader
          updatedAt={safetyData.carrierOverview.dateUpdated}
          dotNumber={team.dot_number!}
        >
          <Text variant="header2" fontColor={primaryBlack}>
            Your fleet’s safety and compliance
          </Text>
        </SafetyDashboardHeader>

        <Box mt={6} />

        <SafetyDashboardOosSummary
          violations={safetyData.violationsDetails}
          onHeaderClick={handleHeaderClick}
          safetyOverview={safetyData.carrierOverview}
        />

        <Box mt={6} />
        <SafetyDashboardViolationsSummary
          violations={safetyData.violationsDetails}
          onViolationClick={setSelectedSection}
        />
      </>
    )
  }

  return (
    <Wrap width="744px" maxWidth="100%" align="left">
      {renderContent()}
    </Wrap>
  )
}

function useTeamSafetyDashboard({ enabled, teamId }: { enabled: boolean; teamId: string }) {
  const { data, isFetching, refetch, isLoading } = useQuery(
    `SAFETY-DASHBOARD-${teamId}`,
    () => getSafetyDashboard(teamId),
    {
      enabled,
      refetchOnWindowFocus: false,
    }
  )

  return {
    safetyData: data,
    refetch,
    isLoading,
    isFetching,
  }
}
